import React, {useRef} from 'react';
import {observer} from 'mobx-react-lite';
import {ClipTypeEnum, Engine} from "@rendley/sdk";
import {Clip, RendleyStore} from "../../../../store/RendleyStore";
import {useCreateDropClipPlaceholder} from "../../hooks/tracks/useCreateDropClipPlaceholder";
import {TRACK_CLASSNAME} from "../../config/constants";
import {ApplicationStore} from "../../../../store/ApplicationStore";
import {RendleyService} from "../../../../services/RendleyService";
import {TimelineService} from "../../services/TimelineService";
import {convertUnitsToTime} from "../../../../utils/dom/convertUnitsToTime";
import './Track.styles.scss';

// Import your clip components here
import Transition from "../Transition/Transition";
import {
    CLIP_LIBRETTO_CLIP_ID,
    GenerateLibrettoClipId,
} from "../../../../Constants";
import LibrettoVideoClipWithWaveform from "../LibrettoVideoClipWithWaveform/LibrettoVideoClipWithWaveform";
import LibrettoAudioClipWithWaveform from "../LibrettoAudioClipWithWaveform/LibrettoAudioClipWithWaveform";
import LibrettoImageClip from "../LibrettoImageClip/LibrettoImageClip";
import LibrettoTextClip from "../LibrettoTextClip/LibrettoTextClip";
import LibrettoShapeClip from "../LibrettoShapeClip/LibrettoShapeClip";
import LibrettoSubtitlesClip from "../LibrettoSubtitlesClip/LibrettoSubtitlesClip";
import LibrettoLottieClip from "../LibrettoLottieClip/LibrettoLottieClip";
import LibrettoHTMLTextClip from "../LibrettoHTMLTextClip/LibrettoHTMLTextClip";

interface TrackProps {
    layerId: string;
    setContextMenuVisible: (visible: boolean) => void;
    setContextMenuPosition: (position: { x: number, y: number }) => void;
    timelineTracksRef: React.RefObject<HTMLDivElement>;
}

export const Track: React.FC<TrackProps> = observer(({ layerId }) => {
    const placeholderRef = useRef<HTMLDivElement>(null);
    const createDropClipPlaceholder = useCreateDropClipPlaceholder(() => placeholderRef.current);

    const layer = RendleyStore.layers[layerId];
    const temporaryTransitions = layer ? TimelineService.getTemporaryTransitions(layerId) : [];
    const clipsIds = layer?.clipsIds ?? [];
    const transitionIds = layer?.transitionIds ?? [];
    const clips = clipsIds.reduce<Record<string, Clip>>((acc, cv) => {
        acc[cv] = RendleyStore.clips[cv];
        return acc;
    }, {});
    const librettoClipIdsOnLayer = RendleyStore.getLibrettoClipIdsForLayer(layerId);

    // Verification: Check that clipIds from layer match those from LibrettoClipIds
    const clipIdsFromLibrettoClips = new Set<string>();
    librettoClipIdsOnLayer.forEach(librettoClipId => {
        const clipIds = RendleyStore.getClipIdsForLibrettoClip(librettoClipId);
        clipIds.forEach(clipId => clipIdsFromLibrettoClips.add(clipId));
    });

    const layerClipIdsSet = new Set(clipsIds);
    const missingInLayer = Array.from(clipIdsFromLibrettoClips).filter(id => !layerClipIdsSet.has(id));
    const missingInLibretto = clipsIds.filter(id => !clipIdsFromLibrettoClips.has(id));

    if (missingInLayer.length > 0 || missingInLibretto.length > 0) {
        console.warn(`[Track ${layerId}] ClipId mismatch detected:`, {
            layerClipIds: clipsIds,
            librettoClipIds: librettoClipIdsOnLayer,
            clipIdsFromLibrettoClips: Array.from(clipIdsFromLibrettoClips),
            missingInLayer,
            missingInLibretto
        });
    }

    // Prepare LibrettoClip groups for rendering
    const librettoClipGroups = librettoClipIdsOnLayer.map(librettoClipId => ({
        librettoClipId,
        clipIds: RendleyStore.getClipIdsForLibrettoClip(librettoClipId)
    }));

    const isScriptLayer = RendleyStore.scriptLayerId === layerId;
    let layerHasWideClip = false;
    for (const [id, clip] of Object.entries(clips) as [string, Clip][]) {
        if (clip.type === ClipTypeEnum.AUDIO || clip.type === ClipTypeEnum.VIDEO) {
            layerHasWideClip = true;
        }
    }

    const trackHeight = isScriptLayer ? "5.25rem" : layerHasWideClip ? "4.25rem" : "2.25rem";

    const handleDrop = async (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();

        // @ts-ignore
        createDropClipPlaceholder.onDragLeave(event);
        let librettoClipId = null;
        try {
            const draggingObject = ApplicationStore.draggingObject;
            let clipId: string | undefined;

            if (!draggingObject) {
                return null;
            }

            if (draggingObject.type === "media") {
                const mediaId = draggingObject.payload.id;
                const startTime = convertUnitsToTime(event.nativeEvent.offsetX);
                const clip = await RendleyService.addMediaToLayer(layerId, mediaId, startTime);
                librettoClipId = clip.getCustomData(CLIP_LIBRETTO_CLIP_ID) as string;
                if (librettoClipId === null || librettoClipId === "" || librettoClipId === undefined) {
                    librettoClipId = GenerateLibrettoClipId();
                    clip.setCustomData(CLIP_LIBRETTO_CLIP_ID, librettoClipId);
                    RendleyStore.addClipIdForLibrettoClip(librettoClipId, clip.id);
                    RendleyStore.setLayerIdForLibrettoClip(librettoClipId, layer.id);
                    RendleyStore.addLibrettoClipIdToLayer(librettoClipId, layer.id);
                }
                clipId = clip?.id;
            }

            if (draggingObject.type === "text-preset") {
                const title = draggingObject.payload;
                const startTime = convertUnitsToTime(event.nativeEvent.offsetX);
                const clip = await RendleyService.createLottieClip({
                    dataUrl: title.dataUrl,
                    propertiesUrl: title.propertiesUrl,
                    startTime,
                    layerId: layerId,
                });
                librettoClipId = clip.getCustomData(CLIP_LIBRETTO_CLIP_ID) as string;
                if (librettoClipId === null || librettoClipId === "" || librettoClipId === undefined) {
                    librettoClipId = GenerateLibrettoClipId();
                    clip.setCustomData(CLIP_LIBRETTO_CLIP_ID, librettoClipId);
                    RendleyStore.addClipIdForLibrettoClip(librettoClipId, clip.id);
                    RendleyStore.setLayerIdForLibrettoClip(librettoClipId, layer.id);
                    RendleyStore.addLibrettoClipIdToLayer(librettoClipId, layer.id);
                }
                clipId = clip?.id;
            }

            if (clipId != null) {
                ApplicationStore.setSelectedLibrettoClipId(librettoClipId);
            }
        } catch {
            return null;
        }
    };

    const ReadableClipType = (clipType: string) => {
        return String(clipType).toLowerCase();
    }

    const renderLibrettoGroup = (clipIds: string[], librettoClipId: string, index: number) => {
        const firstId = clipIds[0];
        const clip = clips[firstId];
        if (!clip) return null;

        switch (ReadableClipType(clip.type)) {
            case ClipTypeEnum.GIF:
            case ClipTypeEnum.VIDEO:
                return (
                    <LibrettoVideoClipWithWaveform
                        key={`${firstId}-${index}`}
                        librettoClipId={librettoClipId}
                        layerId={layerId}
                    />
                );
            case ClipTypeEnum.AUDIO:
                return (
                    <LibrettoAudioClipWithWaveform
                        key={`${firstId}-${index}`}
                        librettoClipId={librettoClipId}
                        layerId={layerId}
                    />
                );
            case ClipTypeEnum.IMAGE:
                return <LibrettoImageClip key={`${firstId}-${index}`} clipIds={clipIds} librettoClipId={librettoClipId} layerId={layerId} />;
            case ClipTypeEnum.HTML_TEXT:
                return <LibrettoHTMLTextClip key={`${firstId}-${index}`} librettoClipId={librettoClipId} layerId={layerId} />;
            case ClipTypeEnum.TEXT:
                return <LibrettoTextClip key={`${firstId}-${index}`} librettoClipId={librettoClipId} layerId={layerId} />;
            case ClipTypeEnum.SHAPE:
                return <LibrettoShapeClip key={`${firstId}-${index}`} librettoClipId={librettoClipId} layerId={layerId} />;
            case ClipTypeEnum.SUBTITLES:
                return <LibrettoSubtitlesClip key={`${firstId}-${index}`} librettoClipId={librettoClipId} layerId={layerId} />;
            case ClipTypeEnum.LOTTIE:
                return <LibrettoLottieClip key={`${firstId}-${index}`} librettoClipId={librettoClipId} layerId={layerId} />;
            default:
                return <LibrettoTextClip key={`${firstId}-${index}`} librettoClipId={librettoClipId} layerId={layerId}/>;
        }
    };

    const renderTransition = (transitionId: string) => {
        return <Transition key={transitionId} type="permanent" permanentTransition={{ transitionId }} />;
    };

    const renderTransitionPlaceholder = ({ startTime, layerId, clipId }: any) => {
        return <Transition key={startTime} type="temporary" temporaryTransition={{ startTime, layerId, clipId }} />;
    };

    const handleDragEnter = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        // @ts-ignore
        return createDropClipPlaceholder.onDragEnter(event);
    };

    const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        // @ts-ignore
        return createDropClipPlaceholder.onDragOver(event);
    };

    const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        // @ts-ignore
        return createDropClipPlaceholder.onDragLeave(event);
    };

    return (
        <div id={layerId} className={`${TRACK_CLASSNAME} ${TRACK_CLASSNAME}-${layerId}`}>
            <div
                className="track__content"
                onDragEnter={handleDragEnter}
                onDragOver={handleDragOver}
                style={{ height: trackHeight}}
                onDrop={handleDrop}
                onDragLeave={handleDragLeave}
            >
                <div ref={placeholderRef} className="track__drop-clip-placeholder"></div>
                {librettoClipGroups.map((group, index) =>
                    renderLibrettoGroup(group.clipIds, group.librettoClipId, index)
                )}
                {transitionIds.map(renderTransition)}
                {temporaryTransitions.map(renderTransitionPlaceholder)}
            </div>
        </div>
    );
});